import { ROUTES, ROUTE_KEYS, ROUTE_LIST, type RouteKey } from './manifest.js';
import { BODY_METHODS, type AnyRoute, type ApiContext, type HttpMethod } from './types.js';

/** `/api/methods/:methodVersionId/graph` → `['methodVersionId']`. */
export const pathParamNames = (path: string): readonly string[] =>
  path
    .split('/')
    .filter((segment) => segment.startsWith(':'))
    .map((segment) => segment.slice(1));

/** OpenAPI/Express template form: `:id` → `{id}`. */
export const toTemplatePath = (path: string): string =>
  path
    .split('/')
    .map((segment) => (segment.startsWith(':') ? `{${segment.slice(1)}}` : segment))
    .join('/');

export const hasRequestBody = (route: AnyRoute): boolean => BODY_METHODS.includes(route.method);

export type RequestInputRecord = Readonly<Record<string, unknown>>;

export interface SplitRequest {
  /** Concrete path with parameters substituted and URL-encoded. */
  readonly path: string;
  /** Query parameters for GET/DELETE; always empty for body methods. */
  readonly query: Readonly<Record<string, unknown>>;
  /** JSON body for POST/PUT/PATCH; `undefined` otherwise. */
  readonly body: RequestInputRecord | undefined;
}

/**
 * Split one request-schema input into its HTTP parts.
 *
 * Body methods send the whole input as the body — path parameters included — so
 * a server may validate the body alone or the merged object and get the same
 * answer. Non-body methods put everything except path parameters in the query.
 */
export const splitRequestInput = (route: AnyRoute, input: RequestInputRecord): SplitRequest => {
  const params = pathParamNames(route.path);
  const path = route.path
    .split('/')
    .map((segment) => {
      if (!segment.startsWith(':')) return segment;
      const name = segment.slice(1);
      const value = input[name];
      if (value === undefined || value === null) {
        throw new Error(`Route ${route.operationId} requires path parameter "${name}"`);
      }
      return encodeURIComponent(String(value));
    })
    .join('/');

  if (hasRequestBody(route)) {
    return { path, query: {}, body: input };
  }

  const query: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (params.includes(key)) continue;
    if (value === undefined || value === null) continue;
    query[key] = value;
  }
  return { path, query, body: undefined };
};

/**
 * Inverse of `splitRequestInput`, for the server: rebuild the single object the
 * request schema describes out of the transport's three buckets.
 */
export const mergeRequestInput = (
  route: AnyRoute,
  parts: {
    readonly params?: Readonly<Record<string, unknown>>;
    readonly query?: Readonly<Record<string, unknown>>;
    readonly body?: Readonly<Record<string, unknown>>;
  },
): Record<string, unknown> =>
  hasRequestBody(route)
    ? { ...parts.body, ...parts.params }
    : { ...parts.query, ...parts.params };

/** `?a=1&b=x&b=y`, or `''` when there is nothing to send. */
export const serialiseQuery = (query: Readonly<Record<string, unknown>>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null) continue;
        search.append(key, String(item));
      }
      continue;
    }
    search.append(key, String(value));
  }
  const serialised = search.toString();
  return serialised.length > 0 ? `?${serialised}` : '';
};

export const isPublicRoute = (route: AnyRoute): boolean => route.roles.length === 0;

export const findRouteKey = (method: HttpMethod, path: string): RouteKey | undefined =>
  ROUTE_KEYS.find((key) => ROUTES[key].method === method && ROUTES[key].path === path);

export const findRoute = (method: HttpMethod, path: string): AnyRoute | undefined => {
  const key = findRouteKey(method, path);
  return key === undefined ? undefined : ROUTES[key];
};

export const routesForContext = (context: ApiContext): readonly AnyRoute[] =>
  ROUTE_LIST.filter((route) => route.context === context);
